import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { format } from "@repo/shared/helpers/format";
import {
  type ColumnFormat,
  columnFormatToPrimitiveType,
} from "@repo/shared/types/column-format";
import type { SortType } from "@repo/shared/types/sort";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type Header,
  type Row,
  type RowData,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { ReactElement, ReactNode, RefObject } from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { cn } from "../lib/utils";
import { Badge } from "../ui/badge";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "../ui/context-menu";
import { Skeleton } from "../ui/skeleton";
import {
  TableBody,
  TableCell,
  Table as TableComponent,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableStickyFooter,
} from "../ui/table";
import { TooltipProvider } from "../ui/tooltip";
import { ColumnLabel } from "./column-label";
import { Icon } from "./icon";
import { TruncateTooltip } from "./truncate-tooltip";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Override the column header label. Falls back to the column id. */
    displayName?: string;
    /** When set, the default cell applies `format()` and the header shows a type icon. */
    format?: ColumnFormat;
    /** When true, shows a grouped indicator icon in the column header. */
    grouped?: boolean;
    /** When set, shows a sort direction icon in the column header. */
    sortType?: SortType;
  }
}

export type TableColumn<TData> = ColumnDef<TData, unknown>;

export interface TableFooterHelpers {
  /** Notify the table that a portal-based overlay (e.g. dropdown menu) inside the footer opened or closed. Keeps the footer visible while any overlay is open. */
  onMenuOpenChange: (open: boolean) => void;
}

export interface TableProps<TData = Record<string, unknown>> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  loading?: boolean;
  className?: string;
  /** Render function for the sticky footer. Receives column widths and helpers to lock visibility while overlays are open. */
  renderFooter?: (
    columns: { id: string; width: number }[],
    helpers: TableFooterHelpers,
  ) => ReactNode;
  /** When true, the footer is always visible. Otherwise it auto-shows on mouse proximity to the bottom edge. */
  footerPinned?: boolean;
  /** Wraps each header cell element, e.g. to add a context menu. Receives the column id and the default header button. */
  renderHeaderWrapper?: (columnId: string, children: ReactElement) => ReactNode;
  /** Controlled sorting state. When provided, the table uses this instead of internal state and skips client-side sorting (data is assumed pre-sorted). */
  sorting?: { id: string; desc: boolean }[];
  /** Called when the user clicks a sortable header. Required alongside `sorting` for controlled mode. */
  onSortingChange?: (sorting: { id: string; desc: boolean }[]) => void;
  /** Global filter string. When provided, rows are filtered client-side across all columns using substring matching. */
  globalFilter?: string;
  /**
   * Advisory shown as a floating pill over the bottom of the table, e.g. that
   * the result was capped. Overlaid rather than laid out, so it never shifts
   * the table's own sizing.
   */
  notice?: string;
  /**
   * Controlled column visibility (column id -> visible). Applied at the
   * visible-cells stage in TanStack, so toggling it is O(columns) and — unlike
   * omitting hidden columns from the `columns` prop — never invalidates the
   * core row model (an O(rows) rebuild). Columns absent from the map are
   * visible by default.
   */
  columnVisibility?: Record<string, boolean>;
  /**
   * Controlled column display order (array of column accessor keys). When
   * provided together with `onColumnOrderChange`, columns are rendered in this
   * order and a drag handle is shown on each header.
   */
  columnOrder?: string[];
  /** Called when the user finishes dragging a column header to a new position. */
  onColumnOrderChange?: (next: string[]) => void;
  /**
   * Controlled per-column widths in pixels (column id -> width), for columns
   * that have an explicit width. Columns absent from this map fall back to the
   * default size. Applied for display even without `onColumnWidthsChange`.
   */
  columnWidths?: Record<string, number>;
  /**
   * Called once when a resize gesture ends, with the full current sizing map.
   * Providing this enables the resize handle on each header. Columns reset to
   * their default size are omitted from the map (so callers can delete them).
   */
  onColumnWidthsChange?: (next: Record<string, number>) => void;
}

const ROW_HEIGHT_PX = 28;
const OVERSCAN = 20;
/** Default column width (px) when no explicit width is set. */
const DEFAULT_COLUMN_WIDTH = 150;
/** Smallest width (px) a column can be resized to. */
const MIN_COLUMN_WIDTH = 80;
/** Largest width (px) a column can be resized to. */
const MAX_COLUMN_WIDTH = 800;

/** Shallow element-wise equality for optional string arrays. */
const arraysEqual = (a?: string[], b?: string[]) =>
  a === b ||
  (!!a && !!b && a.length === b.length && a.every((v, i) => v === b[i]));

/**
 * Renders a value with standard null/empty/boolean styling: null → italic "NULL",
 * empty/undefined/NaN → italic "-", boolean → Badge. Other values are returned as-is (string or ReactNode).
 */
export function renderCellValue(
  value: unknown,
  formatted?: string | ReactNode,
): ReactNode {
  if (value === null) {
    return <span className="text-muted-foreground italic">NULL</span>;
  }
  if (
    value === undefined ||
    value === "" ||
    (typeof value === "number" && Number.isNaN(value))
  ) {
    return <span className="text-muted-foreground italic">-</span>;
  }
  if (typeof value === "boolean") {
    return (
      <Badge variant={value ? "secondary" : "outline"}>
        {value ? "True" : "False"}
      </Badge>
    );
  }
  return formatted !== undefined ? formatted : String(value);
}

/** Returns a plain-text representation of a cell value for clipboard copy. */
const formatCellText = (
  value: unknown,
  columnFormat?: ColumnFormat,
): string => {
  if (value === null) return "NULL";
  if (
    value === undefined ||
    value === "" ||
    (typeof value === "number" && Number.isNaN(value))
  )
    return "";
  if (typeof value === "boolean") return value ? "True" : "False";
  if (columnFormat) return format({ value, format: columnFormat });
  return String(value);
};

const FOOTER_PROXIMITY_PX = 40;

const useBottomProximity = (containerRef: RefObject<HTMLDivElement | null>) => {
  const [visible, setVisible] = useState(false);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setVisible(rect.bottom - e.clientY < FOOTER_PROXIMITY_PX);
    },
    [containerRef],
  );

  const handleMouseLeave = useCallback(() => setVisible(false), []);

  return { visible, handleMouseMove, handleMouseLeave };
};

/**
 * Owns the footer proximity-detection state so that toggling footer visibility
 * does not re-render the table body passed as `children`.
 */
const TableFooterContainer = ({
  children,
  renderFooter,
  footerColumns,
  footerPinned,
  scrollRef,
  className,
}: {
  children: ReactNode;
  renderFooter: (
    columns: { id: string; width: number }[],
    helpers: TableFooterHelpers,
  ) => ReactNode;
  footerColumns: { id: string; width: number }[];
  footerPinned: boolean;
  scrollRef: RefObject<HTMLDivElement | null>;
  className?: string;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const menuOpenCount = useRef(0);
  const [menuLocked, setMenuLocked] = useState(false);
  const {
    visible: footerVisible,
    handleMouseMove,
    handleMouseLeave,
  } = useBottomProximity(wrapperRef);

  const onMenuOpenChange = useCallback((open: boolean) => {
    menuOpenCount.current += open ? 1 : -1;
    setMenuLocked(menuOpenCount.current > 0);
  }, []);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    const footerEl = footerRef.current;
    if (!scrollEl || !footerEl) return;
    const syncScroll = () => {
      footerEl.scrollLeft = scrollEl.scrollLeft;
    };
    scrollEl.addEventListener("scroll", syncScroll, { passive: true });
    return () => scrollEl.removeEventListener("scroll", syncScroll);
  }, [scrollRef]);

  const helpers = useMemo<TableFooterHelpers>(
    () => ({ onMenuOpenChange }),
    [onMenuOpenChange],
  );

  const footerContent = useMemo(
    () => renderFooter(footerColumns, helpers),
    [renderFooter, footerColumns, helpers],
  );

  return (
    <div
      ref={wrapperRef}
      className={cn("flex h-full min-h-0 w-full flex-col", className)}
      onMouseMove={footerPinned ? undefined : handleMouseMove}
      onMouseLeave={footerPinned ? undefined : handleMouseLeave}
    >
      {children}
      <TableStickyFooter
        ref={footerRef}
        pinned={footerPinned}
        visible={footerVisible || menuLocked}
        className="overflow-hidden"
      >
        {footerContent}
      </TableStickyFooter>
    </div>
  );
};

/**
 * Drag-to-resize grabber pinned to a header cell's right edge. Stops pointer
 * propagation so it never starts a dnd-kit column drag, supports mouse and
 * touch, and resets the column to its default width on double-click.
 */
const ColumnResizeHandle = ({ header }: { header: Header<any, unknown> }) => {
  const resizeHandler = header.getResizeHandler();
  return (
    <div
      // dnd-kit's PointerSensor binds `onPointerDown` to the whole header cell,
      // so we must stop the pointer event here too — otherwise resizing also
      // starts a column drag. Mouse/touch handlers below drive the actual
      // TanStack resize.
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => {
        e.stopPropagation();
        resizeHandler(e);
      }}
      onTouchStart={(e) => {
        e.stopPropagation();
        resizeHandler(e);
      }}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        header.column.resetSize();
      }}
      className={cn(
        "absolute top-0 right-0 z-1 -mr-1 flex h-full w-2 cursor-col-resize touch-none select-none items-center justify-center",
        "before:h-3.5 before:w-px before:bg-border hover:before:bg-foreground/40",
        header.column.getIsResizing() &&
          "before:bg-primary hover:before:bg-primary",
      )}
    />
  );
};

/**
 * A `<TableHead>` cell that can be dragged horizontally to reorder columns.
 * Only rendered when `onColumnOrderChange` is provided on the parent `Table`.
 */
const SortableTableHead = ({
  columnId,
  style,
  className,
  children,
}: {
  columnId: string;
  style?: React.CSSProperties;
  className?: string;
  children: ReactNode;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: columnId });

  const dragStyle: React.CSSProperties = {
    ...style,
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    zIndex: isDragging ? 1 : undefined,
    cursor: isDragging ? "grabbing" : undefined,
  };

  return (
    <TableHead
      ref={setNodeRef}
      style={dragStyle}
      className={className}
      {...attributes}
      {...listeners}
      // dnd-kit's `attributes` adds `tabIndex={0}` (for keyboard drag), which
      // would make the cell a second tab stop alongside the inner header
      // button. Keep a single focusable element per header; mouse drag still
      // works via the pointer sensor.
      tabIndex={-1}
    >
      {children}
    </TableHead>
  );
};

/**
 * A single virtualized data row. Memoized so that shifting the virtual window
 * (the common scroll case) only mounts/unmounts the rows entering/leaving the
 * window instead of re-rendering every visible row. `row` is referentially
 * stable per row-model computation; `renderEpoch` is a token whose identity
 * changes on every `Table` render (but stays stable during scroll, which
 * re-renders only `VirtualizedTableBody`). It busts the memo whenever the table
 * config changes — column format/meta, order, visibility, or width — none of
 * which alter the identity-stable `row` object, so cells re-read the latest
 * `getVisibleCells()` / `column.getSize()` / `columnDef.meta`.
 *
 * `"use no memo"` is REQUIRED: the app builds with the React Compiler, which
 * otherwise caches the rendered cell elements keyed on `row` alone. Because
 * TanStack `row` has interior mutability — `getVisibleCells()`, `getSize()`,
 * and `columnDef.meta` change without a new `row` identity — that cache would
 * reuse stale cells and silently defeat the `renderEpoch` memo-buster (e.g.
 * changing a column's format would not update the body until a remount). The
 * explicit `memo` + `renderEpoch` prop below is the sole memoization layer.
 */
const VirtualTableRow = memo(
  ({
    row,
    rowIndex,
    size,
    start,
  }: {
    row: Row<any>;
    rowIndex: number;
    size: number;
    start: number;
    /** Only present to invalidate the memo on any config-driven Table render. */
    renderEpoch: object;
  }) => {
    "use no memo";
    return (
      <TableRow
        data-row-index={rowIndex}
        className="absolute w-full"
        style={{
          height: `${size}px`,
          transform: `translateY(${start}px)`,
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            data-col-id={cell.column.id}
            style={{
              width: cell.column.getSize(),
            }}
          >
            <TruncateTooltip>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TruncateTooltip>
          </TableCell>
        ))}
      </TableRow>
    );
  },
);

/**
 * Owns the row virtualizer and renders the virtual window, so scroll-driven
 * updates re-render only this component (not the parent `Table` with its
 * headers, footer, and dnd-kit contexts). A single context menu wraps the whole
 * body — the right-clicked cell is resolved from `data-*` attributes on demand,
 * avoiding a per-cell menu tree (~columns x rows of them otherwise).
 */
const VirtualizedTableBody = ({
  rows,
  scrollElement,
  renderEpoch,
}: {
  rows: Row<any>[];
  /**
   * The scroll container element (not a ref). Passing the resolved element —
   * seeded from a state-backed callback ref on the parent — guarantees this
   * body re-renders once the container mounts, so the virtualizer attaches
   * even when the table renders in a single pass (e.g. re-entering a board
   * with a fully-cached result). A plain ref would still be `null` during this
   * child's mount-time layout effect (React attaches the ancestor ref after),
   * leaving the virtualizer permanently unsubscribed: headers over an empty
   * body until the next render.
   */
  scrollElement: HTMLDivElement | null;
  /** Per-`Table`-render token forwarded to each row to bust its memo. */
  renderEpoch: object;
}) => {
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_HEIGHT_PX,
    overscan: OVERSCAN,
  });

  const virtualRows = virtualizer.getVirtualItems();

  // Resolve the right-clicked cell lazily from the event target. A ref (not
  // state) avoids a re-render on every right-click; the menu item reads it at
  // click time.
  const menuTargetRef = useRef<{ value: unknown; format?: ColumnFormat }>({
    value: null,
  });

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const cellEl = target.closest("[data-col-id]");
      const rowEl = target.closest("[data-row-index]");
      const colId = cellEl?.getAttribute("data-col-id");
      const rowIndexAttr = rowEl?.getAttribute("data-row-index");
      if (!colId || rowIndexAttr == null) {
        menuTargetRef.current = { value: null };
        return;
      }
      const row = rows[Number(rowIndexAttr)];
      const cell = row?.getVisibleCells().find((c) => c.column.id === colId);
      menuTargetRef.current = {
        value: cell?.getValue(),
        format: cell?.column.columnDef.meta?.format,
      };
    },
    [rows],
  );

  return (
    <ContextMenu>
      <ContextMenuTrigger
        render={
          <TableBody
            className="relative grid"
            style={{
              height: `${virtualizer.getTotalSize()}px`, //tells scrollbar how big the table is
            }}
            onContextMenu={handleContextMenu}
          >
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index] as Row<any>;
              return (
                <VirtualTableRow
                  key={row.id}
                  row={row}
                  rowIndex={virtualRow.index}
                  size={virtualRow.size}
                  start={virtualRow.start}
                  renderEpoch={renderEpoch}
                />
              );
            })}
          </TableBody>
        }
      />
      <ContextMenuContent className="min-w-0">
        <ContextMenuItem
          onClick={() => {
            const { value, format } = menuTargetRef.current;
            navigator.clipboard.writeText(formatCellText(value, format));
            toast.success("Copied to clipboard");
          }}
        >
          <Icon name="copy" className="size-3.5" />
          Copy value
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

export function Table<TData = Record<string, unknown>>({
  data,
  columns,
  loading = false,
  className,
  renderFooter,
  footerPinned = false,
  renderHeaderWrapper,
  sorting: sortingProp,
  onSortingChange: onSortingChangeProp,
  globalFilter,
  notice,
  columnVisibility,
  columnOrder,
  onColumnOrderChange,
  columnWidths,
  onColumnWidthsChange,
}: TableProps<TData>) {
  const enableColumnDrag =
    columnOrder !== undefined && onColumnOrderChange !== undefined;
  const enableColumnResize = onColumnWidthsChange !== undefined;
  // When a header wrapper is provided (e.g. the column options menu), the
  // header button fills the entire cell so the wrapper's right-click target
  // covers the whole cell and there is a single focusable element per header.
  const fullBleedHeader = renderHeaderWrapper !== undefined;
  // Hold the scroll container in state (via a callback ref) so its arrival
  // re-renders the virtualized body and the virtualizer attaches deterministically.
  // `scrollRef` is retained because the footer's scroll-sync effect reads the
  // element imperatively.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const setContainerRef = useCallback((el: HTMLDivElement | null) => {
    scrollRef.current = el;
    setScrollElement(el);
  }, []);
  const isControlledSorting = sortingProp !== undefined;
  const [internalSorting, setInternalSorting] = useState<
    { id: string; desc: boolean }[]
  >([]);
  const sorting = isControlledSorting ? sortingProp : internalSorting;
  const setSorting = isControlledSorting
    ? (onSortingChangeProp ?? (() => {}))
    : setInternalSorting;

  // Local sizing state seeded from the controlled `columnWidths` prop. Kept
  // local so resizes are smooth (live `onChange` feedback); the parent is
  // notified only when a gesture ends (see the resize-end effect below).
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>(
    columnWidths ?? {},
  );

  // Local order state seeded from the controlled `columnOrder` prop. Updated
  // optimistically on drop so the dropped header stays put immediately; the
  // persisted order round-trips back through the prop a render later as an
  // equal no-op. `draggingRef` blocks an external prop change from re-seeding
  // mid-drag.
  const [internalColumnOrder, setInternalColumnOrder] = useState<
    string[] | undefined
  >(columnOrder);
  const draggingRef = useRef(false);
  useEffect(() => {
    if (draggingRef.current) return;
    setInternalColumnOrder((prev) =>
      arraysEqual(prev, columnOrder) ? prev : columnOrder,
    );
  }, [columnOrder]);

  const defaultColumn = useMemo<Partial<ColumnDef<any, unknown>>>(
    () => ({
      minSize: MIN_COLUMN_WIDTH,
      maxSize: MAX_COLUMN_WIDTH,
      size: DEFAULT_COLUMN_WIDTH,
      header: ({ column }) => {
        const meta = column.columnDef.meta;
        if (meta?.format) {
          const sorted = column.getIsSorted();
          const activeSortType: SortType | undefined = sorted
            ? sorted === "asc"
              ? "ASC"
              : "DESC"
            : meta.sortType;
          return (
            <ColumnLabel
              displayName={meta.displayName ?? column.id}
              format={meta.format}
              grouped={meta.grouped ?? false}
              sortType={activeSortType ?? null}
              type={columnFormatToPrimitiveType(meta.format)}
            />
          );
        }
        return (
          <TruncateTooltip>{meta?.displayName ?? column.id}</TruncateTooltip>
        );
      },
      cell: ({ getValue, column }) => {
        const value = getValue();
        const columnFormat = column.columnDef.meta?.format;
        if (columnFormat) {
          return renderCellValue(
            value,
            format({ value, format: columnFormat }),
          );
        }
        return renderCellValue(value);
      },
    }),
    [],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter: globalFilter ?? "",
      ...(columnVisibility !== undefined ? { columnVisibility } : {}),
      ...(internalColumnOrder !== undefined
        ? { columnOrder: internalColumnOrder }
        : {}),
      columnSizing,
    },
    onSortingChange: (updater) => {
      setSorting(typeof updater === "function" ? updater(sorting) : updater);
    },
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(isControlledSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
    defaultColumn,
    columnResizeMode: "onChange",
    enableColumnResizing: true,
  });

  // Persist on gesture end: when the active resize column transitions back to
  // null, notify the parent with the final sizing map (one write per drag).
  const isResizingColumn = table.getState().columnSizingInfo.isResizingColumn;
  const resizingRef = useRef<string | false>(false);
  const wasResizingRef = useRef<string | false>(false);
  resizingRef.current = isResizingColumn;
  useEffect(() => {
    if (wasResizingRef.current && !isResizingColumn) {
      onColumnWidthsChange?.(table.getState().columnSizing);
    }
    wasResizingRef.current = isResizingColumn;
  }, [isResizingColumn, onColumnWidthsChange, table]);

  // Re-seed local sizing only when the external prop changes (cross-device
  // sync / reset). Crucially this does NOT depend on `isResizingColumn`: on
  // resize release the local state already holds the final width from the live
  // drag, and the committed width round-trips back into `columnWidths` a render
  // later as an identical no-op. Depending on the release transition would
  // re-seed from the still-stale prop and flash the column back to its old
  // width. The ref guard still blocks remote updates mid-drag.
  useEffect(() => {
    if (resizingRef.current) return;
    setColumnSizing((prev) => {
      const next = columnWidths ?? {};
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      const same =
        prevKeys.length === nextKeys.length &&
        nextKeys.every((k) => prev[k] === next[k]);
      return same ? prev : next;
    });
  }, [columnWidths]);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleColumnDragStart = () => {
    draggingRef.current = true;
  };

  const handleColumnDragEnd = (event: DragEndEvent) => {
    draggingRef.current = false;
    const { active, over } = event;
    if (
      !over ||
      active.id === over.id ||
      !internalColumnOrder ||
      !onColumnOrderChange
    )
      return;
    const oldIndex = internalColumnOrder.indexOf(String(active.id));
    const newIndex = internalColumnOrder.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(internalColumnOrder, oldIndex, newIndex);
    setInternalColumnOrder(next); // optimistic — header stays in dropped slot
    onColumnOrderChange(next); // persist; round-trips back as an equal no-op
  };

  const { rows } = table.getRowModel();

  // New identity on every `Table` render, forwarded to each memoized row to
  // bust its memo. `Table` only re-renders on config-driven changes (column
  // format/meta, order, visibility, width, sort, filter) — scroll re-renders
  // only `VirtualizedTableBody`, where this reference stays stable — so rows
  // stay memoized while scrolling but always pick up the latest config.
  const renderEpoch = {};

  const hasActiveFilter = !!globalFilter && data.length > 0;
  const isEmpty = !loading && rows.length === 0;
  const emptyMessage = hasActiveFilter
    ? "No matching rows"
    : "No rows to display";

  if (loading && !data.length && !columns.length) {
    return (
      <div className="relative flex h-full w-full flex-col gap-1 bg-background px-2 py-1 font-normal text-sm">
        <Skeleton className="h-7 w-full shrink-0" />
        <div className="min-h-0 flex-1">
          <Skeleton className="h-full w-full" />
        </div>
      </div>
    );
  }

  const tableContent = (
    <TooltipProvider delay={400}>
      <TableContainer
        ref={setContainerRef}
        className={cn(
          "overlay-scrollbar min-h-0 w-full flex-1 overflow-auto",
          renderFooter ? undefined : className,
        )}
      >
        <TableComponent className="grid">
          <TableHeader className="sticky top-0 z-1 grid bg-background">
            {table.getHeaderGroups().map((headerGroup) => {
              const headerRow = (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const headerContent = header.isPlaceholder
                      ? null
                      : (() => {
                          const headerButton = (
                            <button
                              type="button"
                              className={cn(
                                "flex min-w-0 items-center gap-1 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-inset",
                                header.column.getCanSort() &&
                                  "cursor-pointer select-none",
                                fullBleedHeader && "h-full w-full px-2",
                              )}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                              {!header.column.columnDef.meta?.format &&
                                header.column.getIsSorted() && (
                                  <Icon.SortType
                                    sortType={
                                      header.column.getIsSorted() === "asc"
                                        ? "ASC"
                                        : "DESC"
                                    }
                                    className="size-3.5 shrink-0 text-muted-foreground"
                                  />
                                )}
                            </button>
                          );
                          return renderHeaderWrapper
                            ? renderHeaderWrapper(
                                header.column.id,
                                headerButton,
                              )
                            : headerButton;
                        })();

                    if (enableColumnDrag) {
                      return (
                        <SortableTableHead
                          key={header.id}
                          columnId={header.column.id}
                          style={{ width: header.getSize() }}
                          className={fullBleedHeader ? "p-0" : undefined}
                        >
                          {headerContent}
                          {enableColumnResize && (
                            <ColumnResizeHandle header={header} />
                          )}
                        </SortableTableHead>
                      );
                    }

                    return (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{
                          width: header.getSize(),
                          position: enableColumnResize ? "relative" : undefined,
                        }}
                        className={fullBleedHeader ? "p-0" : undefined}
                      >
                        {headerContent}
                        {enableColumnResize && (
                          <ColumnResizeHandle header={header} />
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              );

              if (enableColumnDrag && internalColumnOrder) {
                return (
                  <DndContext
                    key={headerGroup.id}
                    sensors={dndSensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleColumnDragStart}
                    onDragEnd={handleColumnDragEnd}
                    modifiers={[restrictToHorizontalAxis]}
                    accessibility={{
                      container:
                        typeof document !== "undefined"
                          ? document.body
                          : undefined,
                    }}
                  >
                    <SortableContext
                      items={internalColumnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerRow}
                    </SortableContext>
                  </DndContext>
                );
              }

              return headerRow;
            })}
          </TableHeader>
          <VirtualizedTableBody
            rows={rows}
            scrollElement={scrollElement}
            renderEpoch={renderEpoch}
          />
        </TableComponent>
        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
            <p className="font-medium text-muted-foreground text-sm">
              {emptyMessage}
            </p>
          </div>
        )}
        {notice && !isEmpty && (
          <div className="pointer-events-none sticky bottom-2 left-0 z-1 flex justify-center">
            <span className="rounded-full border bg-background/90 px-2 py-0.5 text-muted-foreground text-xs shadow-sm">
              {notice}
            </span>
          </div>
        )}
      </TableContainer>
    </TooltipProvider>
  );

  if (!renderFooter) return tableContent;

  return (
    <TableFooterContainer
      renderFooter={renderFooter}
      footerColumns={table.getVisibleLeafColumns().map((col) => ({
        id: col.id,
        width: col.getSize(),
      }))}
      footerPinned={footerPinned}
      scrollRef={scrollRef}
      className={className}
    >
      {tableContent}
    </TableFooterContainer>
  );
}
